import { Buffer } from "buffer";
import clsx from "clsx";
import { FC, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  PreloadedQuery,
  useLazyLoadQuery,
  usePreloadedQuery,
  useQueryLoader,
  useSubscription,
} from "react-relay";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { graphql } from "relay-runtime";
import {
  AssetLabel,
  i18n,
  IconDenom,
  useLocalStorage,
  useWindowSize,
  Warning,
} from "rujira.ui";
import exclamation from "rujira.ui/src/assets/images/exclamation.gif";
import { AngleDown } from "rujira.ui/src/components/icons/Icons";
import { Drawer } from "vaul";
import { NotFound } from "../common/NotFound";
import {
  TradeQuery,
  TradeQuery$data,
} from "./__generated__/TradeQuery.graphql";
import { TradeRootQuery } from "./__generated__/TradeRootQuery.graphql";
import { OrderBookFragment$key } from "./components/__generated__/OrderBookFragment.graphql";
import { TradeContext } from "./components/Context";
import { Header, MarketSummaryFallback } from "./components/Header";
import { History, HistoryFallback } from "./components/History";
import { Markets } from "./components/Markets";
import {
  BookFilter,
  BookFilterOption,
  OrderBook,
  OrderBookFallback,
} from "./components/OrderBook";
import { Orders, OrdersFallback } from "./components/Orders";
import { Submit } from "./components/Submit";
import { TradingView, TradingViewLoader } from "./components/TradingView";

export const TradeBreakpoint = 1170; // px, used in scss/_trade.scss

const isAsset = (
  str: string,
  asset: { chain: string; type: string; metadata: { symbol: string } }
): boolean => {
  const parts = str.split(/[\.\-]/);

  if (str === "USDC")
    return asset.metadata.symbol === "USDC" && asset.chain === "ETH";

  if (parts.length === 1)
    return (
      (asset.chain === "THOR" && parts[0] === asset.metadata.symbol) ||
      (parts[0] === asset.chain && parts[0] === asset.metadata.symbol)
    );
  return parts[1] === asset.chain && parts[0] === asset.metadata.symbol;
};

const toAsset = (
  id: string
): { chain: string; type: string; metadata: { symbol: string } } => {
  const parts = id.split(/[\.\-]/);
  if (parts.length === 1) {
    return { chain: id, type: "LAYER_1", metadata: { symbol: id } };
  }

  return {
    chain: parts[0],
    type: "LAYER_1",
    metadata: { symbol: parts.slice(1, -1).join("-") },
  };
};

export const toFinAssetId = (asset: {
  chain: string;
  metadata: { symbol: string };
}): string => {
  if (asset.chain === asset.metadata.symbol || asset.chain === "THOR")
    return asset.metadata.symbol;
  if (asset.chain === "ETH" && asset.metadata.symbol === "USDC") return "USDC";
  return `${asset.metadata.symbol}.${asset.chain}`;
};

export const query = graphql`
  query TradeQuery($id: ID!) {
    pair: node(id: $id) @catch {
      ... on FinPair {
        address
        tick
        assetBase {
          chain
          metadata {
            symbol
          }
          ...msgAssetFragment
        }
        assetQuote {
          chain
          metadata {
            symbol
          }
          ...msgAssetFragment
        }
        oracleBase {
          id
        }
        oracleQuote {
          id
        }
        book {
          ...OrderBookFragment
        }
        ...HeaderTradeFragment
        ...SubmitFragment
        trades(first: 100) @connection(key: "HistoryFragment_trades") {
          __id
          edges {
            ...HistoryFragment
          }
        }
      }
    }
  }
`;

// TODO Remove as below
export const rootQuery = graphql`
  query TradeRootQuery {
    finV2(first: 100, sortBy: NAME, sortDir: ASC) {
      edges {
        node {
          address
          tick
          assetBase {
            chain
            type
            metadata {
              symbol
            }
          }
          assetQuote {
            chain
            type
            metadata {
              symbol
            }
          }
        }
      }
    }
  }
`;

const subscription = graphql`
  subscription TradeHistorySubscription($connection: ID!, $prefix: String!) {
    edge(prefix: $prefix) @prependEdge(connections: [$connection]) {
      cursor
      node {
        ... on FinTrade {
          ...HistoryItemFragment
        }
      }
    }
  }
`;

const Meta = () => {
  return (
    <Helmet>
      <title>Rujira Trade</title>
      <meta
        name="description"
        content="Buy & sell tokens on a fully on-chain order book exchange"
      />
      <meta
        name="og:description"
        content="Buy & sell tokens on a fully on-chain order book exchange"
      />
      <meta
        name="twitter:description"
        content="Buy & sell tokens on a fully on-chain order book exchange"
      />
    </Helmet>
  );
};

export const Trade: FC = () => {
  //return <Fallback />;
  return (
    <Suspense fallback={<Fallback />}>
      <QueryProvider />
    </Suspense>
  );
};

const QueryProvider = () => {
  const data = useLazyLoadQuery<TradeRootQuery>(rootQuery, {});
  const { base, quote } = useParams();
  // TODO support eg `FinPair:BTC/ETH.USDT` so that we don't need the root query to return
  // in order to start this one
  const pair =
    data?.finV2 && base && quote
      ? data?.finV2?.edges?.find(
          (x) =>
            x?.node &&
            isAsset(base, x.node.assetBase) &&
            isAsset(quote, x.node.assetQuote)
        )
      : null;

  const assetBase = toAsset(base || "");
  const assetQuote = toAsset(quote || "");

  const [q, loadQuery] = useQueryLoader<TradeQuery>(query);

  const load = useCallback(() => {
    pair?.node?.address &&
      loadQuery(
        {
          id: Buffer.from(`FinPair:${pair.node.address}`).toString("base64"),
        },
        { fetchPolicy: "store-and-network" }
      );
  }, [pair]);

  useEffect(() => {
    if (!load) return;

    load();
    addEventListener("focus", load);
    return () => {
      removeEventListener("focus", load);
    };
  }, [load]);

  return pair?.node ? (
    q ? (
      <Loader
        address={pair.node.address}
        preloaded={q}
        assetBase={assetBase}
        assetQuote={assetQuote}
      />
    ) : (
      <Fallback />
    )
  ) : (
    <NotFound />
  );
};

const Loader: FC<{
  address?: string;
  q?: TradeQuery$data;
  preloaded: PreloadedQuery<TradeQuery>;
  assetBase: { chain: string; type: string; metadata: { symbol: string } };
  assetQuote: { chain: string; type: string; metadata: { symbol: string } };
}> = ({ address, preloaded, assetBase, assetQuote }) => {
  const d = usePreloadedQuery(query, preloaded);

  return (
    <Content
      address={address}
      q={d}
      assetBase={assetBase}
      assetQuote={assetQuote}
    />
  );
};
const Content: FC<{
  address?: string;
  q: TradeQuery$data;
  assetBase: { chain: string; type: string; metadata: { symbol: string } };
  assetQuote: { chain: string; type: string; metadata: { symbol: string } };
}> = ({ address, q, assetBase, assetQuote }) =>
  q.pair.ok ? (
    q.pair.value && address ? (
      <Success address={address} pair={q.pair.value} />
    ) : (
      <NotFound />
    )
  ) : (
    <Error
      assetBase={assetBase}
      assetQuote={assetQuote}
      errors={
        q.pair.errors as readonly { status: string; path: readonly string[] }[]
      }
    />
  );

const Success: FC<{
  address: string;
  pair: NonNullable<Extract<TradeQuery$data["pair"], { ok: true }>["value"]>;
}> = ({ address, pair }) => {
  const { width } = useWindowSize();
  const [tab, setTab] = useState<"graph" | "book" | "history">("graph");
  //const [scrollPosition, setScrollPosition] = useState(0);
  const [showDrawer, setShowDrawer] = useState(false);
  //const ref = useRef<HTMLDivElement | null>(null);

  /* const handleScroll = (e: any) => {
    const { scrollLeft, scrollWidth, clientWidth } = e.target;
    const position = Math.ceil(
      (scrollLeft / (scrollWidth - clientWidth)) * 100
    );
    setScrollPosition(position);
  }; */

  const prefix = Buffer.from(`FinTrade:${address}`).toString("base64");

  const config = useMemo(
    () => ({
      subscription,
      variables: { connection: pair.trades?.__id, prefix },
    }),
    [pair.trades, prefix]
  );
  useSubscription(config);

  return (
    <TradeContext
      address={address}
      base={pair.assetBase?.metadata.symbol || ""}
      quote={pair.assetQuote?.metadata.symbol || ""}>
      <Meta />
      <div className="rujira__main rujira__main--gradient trade">
        {/* <div className="trade__select">
          <Drawer.Root
            direction="left"
            open={showDrawer}
            onOpenChange={(open) => setShowDrawer(open)}>
            <MarketSelect
              k={q?.pair || undefined}
              openDrawer={() => setShowDrawer(true)}
            />
            <Drawer.Portal>
              <Drawer.Overlay className="drawer__overlay" />
              <Drawer.Content className="drawer__content drawer__content--left">
                <Drawer.Title className="visually-hidden">
                  {i18n.t("Markets")}
                </Drawer.Title>
                <div className="drawer__card trade__markets">
                  <Markets closeDrawer={() => setShowDrawer(false)} />
                </div>
              </Drawer.Content>
            </Drawer.Portal>
          </Drawer.Root>
        </div> */}

        <div className="trade__submit">
          <Drawer.Root>
            <Submit k={pair} />
          </Drawer.Root>
        </div>

        <div className="trade__header">
          <Drawer.Root
            direction="left"
            open={showDrawer}
            onOpenChange={(open) => setShowDrawer(open)}>
            <Header openDrawer={() => setShowDrawer(true)} k={pair} />
            <Drawer.Portal>
              <Drawer.Overlay className="drawer__overlay" />
              <Drawer.Content className="drawer__content drawer__content--left">
                <Drawer.Description className="visually-hidden">
                  Markets
                </Drawer.Description>
                <Drawer.Title className="visually-hidden">
                  {i18n.t("Markets")}
                </Drawer.Title>
                <div className="drawer__card trade__markets">
                  <Markets closeDrawer={() => setShowDrawer(false)} />
                </div>
              </Drawer.Content>
            </Drawer.Portal>
          </Drawer.Root>
        </div>
        {width < TradeBreakpoint && (
          <nav className="tabs tabs--sm trade__tabs jc-c">
            <a
              className={clsx({
                tabs__item: true,
                selected: tab === "graph",
              })}
              onClick={() => setTab("graph")}>
              <i18n.label>Chart</i18n.label>
            </a>
            <a
              className={clsx({
                tabs__item: true,
                selected: tab === "book",
              })}
              onClick={() => setTab("book")}>
              <i18n.label>Order Book</i18n.label>
            </a>
            <a
              className={clsx({
                tabs__item: true,
                selected: tab === "history",
              })}
              onClick={() => setTab("history")}>
              <i18n.label>History</i18n.label>
            </a>
          </nav>
        )}
        {(width >= TradeBreakpoint ||
          (width < TradeBreakpoint && tab == "graph")) && (
          <div className="trade__graph">
            <TradingView address={address} tick={Number(pair.tick)} />
          </div>
        )}
        <div className="trade__orders">
          <Orders />
        </div>
        {(width >= TradeBreakpoint ||
          (width < TradeBreakpoint && tab == "book")) && (
          <div className="trade__book">
            <DataView o={pair.book || undefined} />
          </div>
        )}
        {(width >= TradeBreakpoint ||
          (width < TradeBreakpoint && tab == "history")) && (
          <div className="trade__history">
            {pair.trades?.edges?.filter((x) => !!x) && (
              <History
                k={pair.trades?.edges?.filter((x) => !!x) || undefined}
                base={pair.assetBase?.metadata.symbol || ""}
                quote={pair.assetQuote?.metadata.symbol || ""}
                tick={Number(pair.tick)}
              />
            )}
          </div>
        )}
      </div>
    </TradeContext>
  );
};

const Error: FC<{
  errors: readonly { status: string; path: readonly string[] }[];
  assetBase: { chain: string; type: string; metadata: { symbol: string } };
  assetQuote: { chain: string; type: string; metadata: { symbol: string } };
}> = ({ errors, assetBase, assetQuote }) => {
  const { width } = useWindowSize();
  const [showDrawer, setShowDrawer] = useState(false);

  return (
    <TradeContext>
      <Meta />
      <div className="rujira__main rujira__main--gradient trade">
        <div className="trade__submit"></div>
        <div className="trade__header">
          <Drawer.Root
            direction="left"
            open={showDrawer}
            onOpenChange={(open) => setShowDrawer(open)}>
            <button
              className="trade__header-pair"
              onClick={() => {
                setShowDrawer(true);
              }}>
              <IconDenom denom={assetBase.metadata.symbol} className="denom" />
              <IconDenom denom={assetQuote.metadata.symbol} className="denom" />
              <h1 className="mr-1">
                <AssetLabel
                  asset={assetBase}
                  Container={({ children }) => (
                    <small className="color-grey">{children}</small>
                  )}
                />{" "}
                <span>/</span>{" "}
                <AssetLabel
                  asset={assetQuote}
                  Container={({ children }) => (
                    <small className="color-grey">{children}</small>
                  )}
                />{" "}
              </h1>
              <AngleDown />
            </button>
            <Drawer.Portal>
              <Drawer.Overlay className="drawer__overlay" />
              <Drawer.Content className="drawer__content drawer__content--left">
                <Drawer.Description className="visually-hidden">
                  Markets
                </Drawer.Description>
                <Drawer.Title className="visually-hidden">
                  {i18n.t("Markets")}
                </Drawer.Title>
                <div className="drawer__card trade__markets">
                  <Markets closeDrawer={() => setShowDrawer(false)} />
                </div>
              </Drawer.Content>
            </Drawer.Portal>
          </Drawer.Root>
        </div>
        {width < TradeBreakpoint && (
          <nav className="tabs tabs--sm trade__tabs jc-c">
            <a className="tabs__item skeleton w-12" />
            <a className="tabs__item skeleton w-12" />
            <a className="tabs__item skeleton w-12" />
          </nav>
        )}

        <div className="trade__graph">
          <div className="h-full w-full p-0.5 flex ai-c jc-c px-1.5">
            <Warning
              className="warning--sm mt-2 condensed flex ai-c"
              color="orange">
              <img
                src={exclamation}
                alt=""
                className="filter-orange block no-shrink"
                style={{ width: "2.5rem", height: "2.5rem" }}
              />
              <div className="text-left fs-14">
                {errors.map((err) => (
                  <div key={err.status}>
                    {err.path?.join(":")}; {err.status}
                  </div>
                ))}
              </div>
            </Warning>
          </div>
        </div>

        <div className="trade__orders">
          <OrdersFallback />
        </div>
        {width >= TradeBreakpoint && (
          <div className="trade__book">
            <DataViewFallback />
          </div>
        )}
        {width >= TradeBreakpoint && (
          <div className="trade__history">
            <HistoryFallback />
          </div>
        )}
      </div>
    </TradeContext>
  );
};

function Fallback(): JSX.Element {
  const { width } = useWindowSize();

  return (
    <TradeContext>
      <Meta />
      <div className="rujira__main rujira__main--gradient trade">
        <div className="trade__submit"></div>
        <div className="trade__header">
          <MarketSummaryFallback />
        </div>
        {width < TradeBreakpoint && (
          <nav className="tabs tabs--sm trade__tabs jc-c">
            <a className="tabs__item skeleton w-12" />
            <a className="tabs__item skeleton w-12" />
            <a className="tabs__item skeleton w-12" />
          </nav>
        )}

        <div className="trade__graph">
          <div className="h-full w-full p-0.5 flex ai-c jc-c">
            <TradingViewLoader />
          </div>
        </div>

        <div className="trade__orders">
          <OrdersFallback />
        </div>
        {width >= TradeBreakpoint && (
          <div className="trade__book">
            <DataViewFallback />
          </div>
        )}
        {width >= TradeBreakpoint && (
          <div className="trade__history">
            <HistoryFallback />
          </div>
        )}
      </div>
    </TradeContext>
  );
}

enum Tab {
  Book = "book",
  Trades = "trades",
  Both = "both",
}

export const DataView: FC<{
  o?: OrderBookFragment$key;
  isPool?: boolean;
}> = ({ o, isPool = false }) => {
  const [tab, setTab] = useLocalStorage<Tab>("trade-dataview-trades", Tab.Book);
  const [filter, setFilter] = useLocalStorage<BookFilterOption>(
    "trade-dataview-book",
    BookFilterOption.All
  );
  const { width } = useWindowSize();

  useEffect(() => {
    if (width < TradeBreakpoint && tab === Tab.Both) {
      setTab(Tab.Book);
    }
  }, [width, tab]);

  return (
    <>
      <BookFilter setFilter={setFilter} isSkeleton={isPool} />
      <OrderBook filter={filter} k={o} />
      <Tooltip
        id="book-tip"
        className="tooltip"
        place="left"
        style={{ zIndex: 1 }}
      />
    </>
  );
};

export const DataViewFallback: FC = () => {
  return (
    <>
      <BookFilter setFilter={() => {}} isSkeleton={true} />
      <OrderBookFallback />
    </>
  );
};
